import React, { useMemo, useCallback, useState } from 'react'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import './index.css'
import { late, grossPay, totalPay, totalContri, grossBilling } from '../../functions/computations'

import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Typography from '@mui/material/Typography'



const SevenStarJasemPayroll = ({ data = [], store = '' }) => {
    const [zoom, setZoom] = useState(1);

    const handleZoomIn = useCallback(() => {
        setZoom((prevZoom) => Math.min(prevZoom + 0.1, 5)); // Max zoom level of 5
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5)); // Min zoom level of 0.5
    }, []);

    const resetZoom = useCallback(() => {
        setZoom(1)
    }, [])


    // Columns based on provided payroll data structure
    const PayrollTableColumns = useMemo(() => [
        {
            header: 'EMP No', accessorFn: (row) => {
                let index = data.findIndex(rowOnData => rowOnData.id === row.id)
                return index !== -1 ? index + 1 : "N/A"
            }
        },
        { accessorKey: 'employee_name', header: 'Employee Name' },

        {
            accessorKey: 'basic_pay', header: 'Basic Pay', accessorFn: (row) => {
                let basic_pay = Number(row.basic_pay || 0)
                return Number(basic_pay.toFixed(2))
            }
        },
        { accessorKey: 'amount_overtime', header: 'Overtime' },
        { accessorKey: 'amount_rest_days', header: 'Rest Day (amt)' },
        { accessorKey: 'amount_rest_day_overtime', header: 'Rest Day OT (amt)' },
        { accessorKey: 'amount_special_holiday', header: 'Special Holiday (amt)' },
        { accessorKey: 'amount_special_holiday_overtime', header: 'Special Holiday OT (amt)' },

        { accessorKey: 'other_income_non_taxable', header: 'Other Income Non Taxable' },
        {
            header: 'Night Diff (amt)',
            accessorFn: (row) => {
                return parseFloat(Number(row?.amount_night_diff || 0).toFixed(2))
            },
        },
        {
            header: 'Late', accessorFn: (row) => {
                let hoursLate = late(row)
                return Number(hoursLate.toFixed(2))
            },
        },
        {
            header: 'Gross Pay', accessorFn: (row) => {
                return parseFloat(Number(row.gross_pay).toFixed(2))
            },
        },
        { accessorKey: 'amount_13th_month', header: '13th month (amt)' },
        {
            header: 'Total Pay', accessorFn: totalPay,
        },
        { accessorKey: 'sss_er', header: 'SSS (employer)' },
        { accessorKey: 'sss_contribution', header: 'SSS (employee)' },
        { accessorKey: 'nhip_contribution', header: 'NHIP' },
        { accessorKey: 'hdmf_contribution', header: 'Pag-IBIG' },
        {
            header: 'Total Contri', accessorFn: totalContri,
        },
        {
            header: 'Gross Billing', accessorFn: grossBilling,
        }
    ], []);

    const table = useReactTable({
        data: data,
        columns: PayrollTableColumns,
        getCoreRowModel: getCoreRowModel(),
    })

    // Render a simple table using useReactTable

    const rows = table.getRowModel().rows;


    // Assuming PayrollTableColumns is an array of column definitions with 'accessorKey' and 'header'
    const columns = PayrollTableColumns;

    const columnSums = useMemo(() => {
        const sums = {};
        columns.forEach(col => {
            if (col.accessorKey) {
                sums[col.accessorKey] = data.reduce((acc, row) => acc + (Number(row[col.accessorKey]) || 0), 0);
            }
            if (typeof col.accessorFn === 'function') {
                sums[col.header] = data.reduce((acc, row) => acc + (Number(col.accessorFn(row)) || 0), 0);
            }
        });
        return sums;
    }, [columns, data]);
    // Calculate sums for the required columns

    return (
        <div className='payroll-table__container'>
            <Paper>
                <Stack direction='row' sx={{ p: 1, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }} justifyContent='space-between'>
                    <Typography variant="h6" component="div" >
                        Payroll Table
                    </Typography>

                    <Stack direction='row' spacing={1} >
                        <Tooltip title="Zoom In">
                            <IconButton
                                size="small"
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                                }}
                                onClick={handleZoomIn}
                            >
                                <ZoomInIcon fontSize="small" sx={{ color: '#6b7280' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Zoom out">
                            <IconButton
                                onClick={handleZoomOut}
                                size="small"
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                                }}
                            >
                                <ZoomOutIcon fontSize="small" sx={{ color: '#6b7280' }} />
                            </IconButton>
                        </Tooltip>
                        <Button onClick = {resetZoom}>Reset Zoom</Button>
                    </Stack>


                </Stack>
                <div className="payroll-table__wrapper">
                    <table id='payroll_table' className="payroll-table" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                        <thead className="payroll-table__header">
                            <tr>
                                {columns.map((col, idx) => (
                                    <th key={col.accessorKey || col.id || idx}>
                                        {col.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="payroll-table__body">
                            <tr className="special-row">
                                <td colSpan={columns.length} >
                                    {store}
                                </td>

                            </tr>
                            {rows.map((row) => (
                                <tr key={row.id}>
                                    {columns.map((col, idx) => {
                                        console.log('col', col);


                                        let value;
                                        if (typeof col.accessorFn === 'function') {
                                            value = col.accessorFn(row.original);
                                        } else if (col.accessorKey) {
                                            value = row.original[col.accessorKey];
                                            return <td key={col.accessorKey || col.id || idx} style={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                textAlign: typeof value === 'number' ? 'right' : 'left',
                                                maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                            }}>
                                                {value}
                                            </td>
                                        } else {
                                            value = '';
                                        }

                                        console.log(`value for ${col.accessorKey || col.id || idx}`, value);
                                        return (
                                            <td key={col.accessorKey || col.id || idx} style={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                textAlign: typeof value === 'number' ? 'right' : 'left',
                                                maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                            }}>
                                                {Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="payroll-table__footer">
                            <tr>
                                {columns.map((col, idx) => {
                                    let value = '';
                                    if (col.accessorKey) {
                                        value = columnSums[col.accessorKey] || 0;
                                    }
                                    if (typeof col.accessorFn === 'function') {
                                        value = columnSums[col.header] || 0;
                                    }
                                    return (
                                        <td key={col.accessorKey || col.id || idx} style={{
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            textAlign: typeof value === 'number' ? 'right' : 'left',
                                            maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                        }}>
                                            {Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </Paper>
        </div>
    );
}

export default SevenStarJasemPayroll